import request from "../utils/request";
import type { UserRegion } from "./users";

/** Source 绑定的单个标签：标签 ID + 管理员为该 Source 预设的值（可为空列表） */
export interface DocTagBinding {
  TagId: string;
  PresetValues: string[];
}

export type SourcePathAskUserScope = 0 | 1;

export interface SourcePathAskItem {
  ID: string;
  SourceSite: string;
  SourceDrive?: string;
  IncludedPaths: string[];
  ExcludedPaths: string[];
  /** 绑定的文档分类 ID（非名称）。已被删除的分类不会出现在此 */
  DocCategoryIds?: string[];
  /** 绑定的文档标签及预设值。已被删除的标签不会出现在此 */
  DocTagBindings?: DocTagBinding[];
  /** General Source Type 对应的文档分类 ID */
  SourceType?: string;
  /** 与 FrameworkUsers 相同的区域取值 */
  Region?: UserRegion;
  VisibleToUsers?: boolean;
  /** 前台 Ask Sources 页面展示的描述 */
  Remark?: string;
  /** 提供给 AI Agent 的 Source 使用提示，与 Remark 分开持久化 */
  AgentTips?: string;
  /** 0 = AZ Internal Only，1 = Internal & External */
  UserScope?: SourcePathAskUserScope;
  CreateTime?: string;
  UpdateTime?: string;
}

export interface SourcePathAskListResponse {
  Count: number;
  Data: SourcePathAskItem[];
}

export interface SourcePathAskPayload {
  SourceSite: string;
  SourceDrive?: string;
  IncludedPaths: string[];
  ExcludedPaths: string[];
  Remark?: string;
  AgentTips?: string;
  /** 与 FrameworkUsers 相同的区域取值 */
  Region: UserRegion;
  /** 0 = AZ Internal Only，1 = Internal & External */
  UserScope?: SourcePathAskUserScope;
  /** General Source Type 对应的文档分类 ID。更新时传 undefined 表示不变更 */
  SourceType?: string;
  /** 绑定的文档分类 ID。更新时传 undefined 表示不变更，传空数组表示清空 */
  DocCategoryIds?: string[];
  /** 绑定的文档标签及预设值。更新时传 undefined 表示不变更，传空数组表示清空 */
  DocTagBindings?: DocTagBinding[];
}

export interface AskSourceSiteItem {
  ID: string;
  SourceSite: string;
  Remark?: string;
  SharePointSiteUrl?: string;
  IncludedPaths: string[];
}

export interface AskSourceSiteListResponse {
  Count: number;
  Data: AskSourceSiteItem[];
}

export interface GlobalExcludedKeywordsResponse {
  Keywords: string[];
}

const NO_REQUEST_TIMEOUT = { timeout: 0 } as const;

export function listSourcePathAsks() {
  return request.get<SourcePathAskListResponse>("/project/sourcepathask/list");
}

/** 普通登录用户可访问的 Ask Sources 只读列表。 */
export function listAskSourceSites() {
  return request.get<AskSourceSiteListResponse>("/project/askscope/sources");
}

export function createSourcePathAsk(data: SourcePathAskPayload) {
  return request.post<SourcePathAskItem>("/project/sourcepathask/create", data, NO_REQUEST_TIMEOUT);
}

export function updateSourcePathAsk(data: SourcePathAskPayload & { ID: string }) {
  return request.put<SourcePathAskItem>("/project/sourcepathask/update", data, NO_REQUEST_TIMEOUT);
}

export function deleteSourcePathAsk(id: string) {
  return request.delete<{ ID: string }>(`/project/sourcepathask/${id}`, NO_REQUEST_TIMEOUT);
}

export function getGlobalExcludedKeywords() {
  return request.get<GlobalExcludedKeywordsResponse>(
    "/project/sourcepathask/globalexcludedkeywords",
  );
}

export function updateGlobalExcludedKeywords(keywords: string[]) {
  return request.put<GlobalExcludedKeywordsResponse>(
    "/project/sourcepathask/globalexcludedkeywords",
    { Keywords: keywords },
  );
}

export function getStudySourceGlobalExcludedKeywords() {
  return request.get<GlobalExcludedKeywordsResponse>(
    "/project/sourcepathstudy/globalexcludedkeywords",
  );
}

export function updateStudySourceGlobalExcludedKeywords(keywords: string[]) {
  return request.put<GlobalExcludedKeywordsResponse>(
    "/project/sourcepathstudy/globalexcludedkeywords",
    { Keywords: keywords },
  );
}
